// Ký giấy: tải lên file biên bản đã ký rồi gắn vào biên bản thỏa thuận
import UploadForm from "components/shared-components/UploadForm";
import { isAgreementPdfReady } from "hooks/invoiceAgreement/useAgreementPdfUrl";
import {
  AGREEMENT_PDF_STATUS_GENERATED,
  AGREEMENT_REPORT_TITLE,
} from "constants/AgreementReportConstant";
import {
  useAttachAgreement,
  useDownloadAgreement,
  useGetAgreementInfo,
  useUploadAgreementFile,
} from "hooks/invoiceAgreement/useInvoiceAgreement";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

const AgreementSignPaperModal = props => {
  const {
    isOpen,
    toggle,
    invoiceId,
    typeReport,
    agreementId,
    onAttached,
    agreementInfo,
  } = props;

  const [resourceId, setResourceId] = useState(null);
  const [isPdfReady, setIsPdfReady] = useState(false);

  // Chỉ cho tải bản PDF để in ký khi backend đã tạo xong file
  const { mutate: mutateGetAgreementInfo, isLoading: isLoadingCheckPdf } =
    useGetAgreementInfo({
      onSuccess: response => {
        setIsPdfReady(
          response.data?.pdf_status === AGREEMENT_PDF_STATUS_GENERATED
        );
      },
      onError: () => {
        setIsPdfReady(false);
      },
    });

  const { mutate: mutateDownloadAgreement, isLoading: isLoadingDownload } =
    useDownloadAgreement({
      onSuccess: response => {
        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${AGREEMENT_REPORT_TITLE[typeReport] || "bien-ban"}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
    });

  const { mutate: mutateUploadAgreementFile, isLoading: isLoadingUpload } =
    useUploadAgreementFile({
      onSuccess: response => {
        setResourceId(response.data?.id);
      },
      onError: () => {
        setResourceId(null);
      },
    });

  const { mutate: mutateAttachAgreement, isLoading: isLoadingAttach } =
    useAttachAgreement({
      onSuccess: () => {
        if (typeof onAttached === "function") {
          onAttached();
        }
        toggle();
      },
    });

  // Mở modal thì hỏi trạng thái file PDF, đóng modal thì xóa sạch
  useEffect(() => {
    setResourceId(null);
    setIsPdfReady(false);

    if (!isOpen || !invoiceId) {
      return;
    }

    // Modal cha vừa nạp lại biên bản mà PDF đã xong thì dùng luôn, khỏi gọi lại
    if (isAgreementPdfReady(agreementInfo)) {
      setIsPdfReady(true);
      return;
    }

    mutateGetAgreementInfo({
      id: invoiceId,
      type: typeReport,
      agreement_id: agreementId,
    });
  }, [isOpen]);

  const handleFiles = files => {
    setResourceId(null);
    if (files.length === 0) {
      return;
    }

    const bodyFormData = new FormData();
    bodyFormData.append("file", files[0]);
    mutateUploadAgreementFile({ file: bodyFormData });
  };

  const confirmAttach = () => {
    mutateAttachAgreement({
      invoice_id: invoiceId,
      resource_id: resourceId,
      agreement_id: agreementId,
    });
  };

  return (
    <Modal
      size="lg"
      contentClassName="confirm-modal"
      isOpen={isOpen}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      toggle={toggle}
    >
      <div className="modal-content">
        <ModalHeader toggle={toggle}>
          <Label>Tải lên biên bản đã ký giấy</Label>
        </ModalHeader>
        <ModalBody>
          <UploadForm
            multiple={false}
            accept=".pdf"
            handleFiles={files => handleFiles(files)}
            removeFile={() => setResourceId(null)}
          ></UploadForm>
          {isLoadingUpload && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <Spinner size="sm" color="primary" />
              <span>Đang tải file lên...</span>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <div>
            <Button
              type="button"
              color="danger"
              className="btn-label"
              disabled={!isPdfReady || isLoadingCheckPdf || isLoadingDownload}
              onClick={() =>
                mutateDownloadAgreement({ agreement_id: agreementId })
              }
            >
              {isLoadingDownload ? (
                <i className="bx bx-loader bx-spin label-icon" />
              ) : (
                <i className="bx bx-download label-icon" />
              )}
              Tải bản PDF để in ký
            </Button>
            {!isPdfReady && !isLoadingCheckPdf && (
              <div className="text-muted mt-1">
                File PDF chưa được tạo xong, chưa tải xuống được.
              </div>
            )}
          </div>
          <div>
            <Button
              type="button"
              color="secondary"
              className="me-2"
              disabled={isLoadingAttach}
              onClick={toggle}
            >
              Đóng
            </Button>
            <Button
              type="button"
              color="success"
              className="me-2 btn-label"
              disabled={!resourceId || isLoadingUpload || isLoadingAttach}
              onClick={confirmAttach}
            >
              {isLoadingAttach ? (
                <i className="bx bx-loader bx-spin label-icon" />
              ) : (
                <i className="bx bx-save label-icon" />
              )}
              Xác nhận ký giấy
            </Button>
          </div>
        </ModalFooter>
      </div>
    </Modal>
  );
};

AgreementSignPaperModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  invoiceId: PropTypes.any,
  typeReport: PropTypes.string,
  agreementId: PropTypes.number,
  onAttached: PropTypes.func,
  // Biên bản modal cha vừa nạp (agreement-info), có thì bỏ qua lần gọi lại
  agreementInfo: PropTypes.object,
};

export default AgreementSignPaperModal;
